import type { ReactNode } from 'react'
import React, { useLayoutEffect, useRef } from 'react'
import type { PopperChildrenProps, ReferenceChildrenProps } from 'react-popper'
import { Manager, Popper, Reference } from 'react-popper'
import * as S from './styled'
import type { Placement } from './types'

export type TooltipStaticProps = {
  children: ReactNode
}

export const TooltipStatic = ({ children }: TooltipStaticProps) => {
  return <Manager>{children}</Manager>
}

export type TooltipStaticReferenceProps = {
  onMouseEnter: () => void
  onMouseLeave: () => void
  onTouchStart: () => void
  onTouchEnd: () => void
  children: ReactNode
}

const TooltipStaticReference = ({ children, ...props }: TooltipStaticReferenceProps) => {
  return (
    <Reference>
      {({ ref }: ReferenceChildrenProps) => (
        <div ref={ref} {...props}>
          {children}
        </div>
      )}
    </Reference>
  )
}

export type TooltipStaticBodyProps = {
  placement?: Placement
  strategy?: 'absolute' | 'fixed'
  offset?: [number, number]
  withPadding?: boolean
  fluid?: boolean
  children: ReactNode
}

const TooltipStaticBody = ({
  placement = 'bottom',
  strategy = 'absolute',
  offset = [0, 10],
  withPadding = true,
  fluid = false,
  children,
}: TooltipStaticBodyProps) => {
  const positionUpdateRef = useRef<Function | null>(null)

  useLayoutEffect(() => {
    if (positionUpdateRef.current) {
      positionUpdateRef.current()
    }
  }, [children])

  const renderTooltip = ({ ref, style, placement: dataPlacement, update }: PopperChildrenProps) => {
    positionUpdateRef.current = update

    return (
      <S.Tooltip
        ref={ref}
        $placement={dataPlacement}
        $withPadding={withPadding}
        $fluid={fluid}
        style={style}
        role="tooltip"
      >
        {children}
      </S.Tooltip>
    )
  }

  return (
    <Popper
      placement={placement}
      strategy={strategy}
      modifiers={[
        { name: 'offset', options: { offset } },
        { name: 'preventOverflow', options: { padding: 8 } },
      ]}
    >
      {renderTooltip}
    </Popper>
  )
}

TooltipStatic.Body = TooltipStaticBody
TooltipStatic.Reference = TooltipStaticReference
